import { ConfirmationNumberOutlined } from "@mui/icons-material";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { formatCurrency, getVoucherAvailableValue } from "../utils";

const getVoucherDescription = (voucher) => {
  if (!voucher) return "Opcional · el cajero declara el voucher recibido";
  const disponible = getVoucherAvailableValue(voucher, voucher.units ?? voucher.amount);
  if (disponible <= 0) return "El saldo no alcanza para un papel completo";
  if (voucher.type === "PRODUCTO") return `Un papel cubre ${disponible} u. · se agregan al carrito`;
  if (voucher.type === "CATEGORIA") return voucher.wholePaper
    ? `Cubre ${disponible} u. de ${voucher.category}. Los productos se agrupan en orden de incorporación.`
    : `Primeras ${disponible} u. de ${voucher.category} incluidas`;
  return `Un papel descuenta ${formatCurrency(disponible)}`;
};

export default function BenefitSelector({ vouchers, selectedCode, onChange }) {
  const [error, setError] = React.useState("");
  const selectedVoucher = vouchers.find((voucher) => voucher.code === selectedCode) || null;

  const handleChange = (event) => {
    const result = onChange(event.target.value);
    setError(result?.applied === false ? result.message : "");
  };

  return (
    <Box
      data-testid="benefit-selector"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "auto minmax(220px, 310px) minmax(0, 1fr)" },
        alignItems: "center",
        gap: { xs: 0.75, sm: 1.25 },
        mb: 1.35,
        px: 1.25,
        py: 1,
        border: selectedVoucher ? "1px solid #F0C77F" : "1px solid #E2E8F0",
        borderRadius: "13px",
        bgcolor: selectedVoucher ? "#FFF9EF" : "#F8FAFC",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "9px", display: "grid", placeItems: "center", bgcolor: selectedVoucher ? "#FFE8BC" : "#EEF2F7" }}>
          <ConfirmationNumberOutlined sx={{ fontSize: 18, color: selectedVoucher ? "#9A6209" : "#64748B" }} />
        </Box>
        <Box>
          <Typography sx={{ color: "#172033", fontSize: 12, lineHeight: 1.1, fontWeight: 900 }}>Voucher físico</Typography>
          <Typography sx={{ color: "#64748B", fontSize: 10.5 }}>No se escanea</Typography>
        </Box>
      </Box>

      <FormControl size="small" fullWidth>
        <Select
          value={selectedCode}
          onChange={handleChange}
          displayEmpty
          inputProps={{ "aria-label": "Aplicar voucher físico" }}
          data-testid="voucher-select"
          sx={{ height: 38, bgcolor: "#fff", fontSize: 12.5, fontWeight: selectedVoucher ? 800 : 600 }}
        >
          <MenuItem value="">Sin voucher</MenuItem>
          {vouchers.map((voucher) => (
            <MenuItem key={voucher.code} value={voucher.code}
              disabled={getVoucherAvailableValue(voucher, voucher.units ?? voucher.amount) <= 0}>
              {voucher.name}{getVoucherAvailableValue(voucher, voucher.units ?? voucher.amount) <= 0 ? " · No alcanza para un papel" : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography
        role={error ? "alert" : undefined}
        sx={{ color: error ? "#C62828" : selectedVoucher ? "#80520D" : "#64748B", fontSize: 11, fontWeight: error ? 800 : 600 }}
      >
        {error || getVoucherDescription(selectedVoucher)}
      </Typography>
    </Box>
  );
}

BenefitSelector.propTypes = {
  vouchers: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCode: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
